// sla_v1 + mobile_v1: 3-screen flow (list/chat/info) + touch-friendly UI
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../api';
import { connectSSE } from '../lib/sse'; /* sse_client_v1 */
import { fetchConversations } from '../lib/conversations'; /* conv_share_v1 */
import { useAdaptivePoll } from '../lib/useAdaptivePoll'; /* poll_adaptive_v1 */
import { loadReadMarks, saveReadMarks, unreadStatus } from '../lib/readMarks'; /* unread_badge_v1 */
import { playNotifySound, unlockAudio, getSoundSettings, setSoundSettings, SOUND_OPTIONS, requestNotifyPermission, showDesktopNotification, notifyPermission, notifySupported } from '../lib/notifySound'; /* notify_sound_v1 + notify_desktop_v1 */

import toast from 'react-hot-toast';
import { Send, Clock, MessageSquare, Ban, Zap, X, Paperclip, Image, FileText, ChevronLeft, MoreVertical, Bell, BellOff, Volume2, Plus, Search } from 'lucide-react';
// getFileUrl_v2: append JWT token for /api/messages/.../file URLs (browser cannot send Auth header for img tags)
const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    const token = localStorage.getItem('token');
    return token ? `${window.location.origin}${url}?t=${encodeURIComponent(token)}` : url;
  }
  return url;
};

// avatar_v1: Reusable avatar — แสดงรูปโปรไฟล์ลูกค้า + fallback initial
function Avatar({ name, pic, size, color, fontSize, mobile, channelType }) {
  const [imgError, setImgError] = useState(false);
  const baseStyle = {
    width: size, height: size, borderRadius: '50%',
    flexShrink: 0, objectFit: 'cover',
  };
  if (pic && !imgError) {
    return (
      <img src={pic} alt={name || 'avatar'} loading="lazy"
        onError={() => setImgError(true)}
        style={baseStyle} />
    );
  }
  return (
    <div style={{
      ...baseStyle,
      background: (color || '#0ea5e9') + '20',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, color: color || '#0ea5e9',
      fontSize: fontSize || Math.round(size * 0.42),
    }}>
      {(name || 'U')[0].toUpperCase()}
    </div>
  );
}

/* slip_retry_v1: ลองดึงผลตรวจซ้ำได้กี่ครั้ง และเว้นห่างเท่าไหร่ */
const SLIP_MAX_ATTEMPTS = 5;
const SLIP_RETRY_INTERVAL = 10000;

// slip_v1: badge/button render ใต้รูปลูกค้า
function SlipStatus({ messageId, mode, hasToken, verification, onVerify, onShowDetail, busy, quotaExceeded, stalled }) {
  if (!hasToken || mode === 'off') return null;
  // slip_quota_v1: quota หมด → แสดงปุ่มสีเทา + แจ้งเตือน
  if (!verification && quotaExceeded) {
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        marginTop: 6, padding: '5px 10px', borderRadius: 6,
        background: '#f1f5f9', color: '#64748b', fontSize: 12, fontWeight: 600,
      }}>
        ⛔ โควต้าหมด — ติดต่อแอดมิน
      </div>
    );
  }
  // Has result → show badge
  if (verification) {
    const v = verification;
    // slip_fraud_check_v1: รองรับ status fraud/invalid
    const bg = v.status === 'success' ? '#dcfce7'
             : v.status === 'mismatch' ? '#fef3c7'
             : v.status === 'fraud' ? '#fee2e2'
             : v.status === 'invalid' ? '#f1f5f9'
             : '#fee2e2';
    const fg = v.status === 'success' ? '#166534'
             : v.status === 'mismatch' ? '#92400e'
             : v.status === 'fraud' ? '#991b1b'
             : v.status === 'invalid' ? '#475569'
             : '#991b1b';
    const icon = v.status === 'success' ? '✅'
               : v.status === 'mismatch' ? '⚠️'
               : v.status === 'fraud' ? '🚫'
               : v.status === 'invalid' ? '❓'
               : '❌';
    const label = v.status === 'success' ? 'สลิปถูกต้อง'
                : v.status === 'mismatch' ? 'บัญชีไม่ตรง'
                : v.status === 'fraud' ? 'สลิปปลอม'
                : v.status === 'invalid' ? 'ไม่ใช่สลิป'
                : (v.error_message || 'ตรวจไม่ผ่าน');
    return (
      <button onClick={onShowDetail}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          marginTop: 6, padding: '5px 10px', borderRadius: 6,
          background: bg, color: fg, border: 'none', cursor: 'pointer',
          fontSize: 12, fontWeight: 600,
        }}>
        {icon} {label}
        {v.amount ? ` • ฿${Number(v.amount).toLocaleString()}` : ''}
      </button>
    );
  }
  // No result + manual mode → show "ตรวจสลิป" button
  if (mode === 'manual') {
    return (
      <button onClick={onVerify} disabled={busy}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          marginTop: 6, padding: '5px 10px', borderRadius: 6,
          background: '#0ea5e9', color: '#fff', border: 'none',
          cursor: busy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600,
          opacity: busy ? 0.6 : 1,
        }}>
        {busy ? '⏳ กำลังตรวจ...' : '🔍 ตรวจสลิป'}
      </button>
    );
  }
  // Auto mode + no result yet (still processing)
  if (mode === 'auto') {
    /* slip_retry_v1: ลองดึงผลจนครบแล้วยังไม่มา — เลิกหมุนหลอก เปลี่ยนเป็นให้กดตรวจเอง */
    if (stalled) {
      return (
        <div style={{ marginTop: 6 }}>
          <button onClick={onVerify} disabled={busy}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', borderRadius: 6,
              background: '#0ea5e9', color: '#fff', border: 'none',
              cursor: busy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600,
              opacity: busy ? 0.6 : 1,
            }}>
            {busy ? '⏳ กำลังตรวจ...' : '🔍 ตรวจสลิป'}
          </button>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
            ยังไม่ได้รับผลตรวจอัตโนมัติ — กดเพื่อตรวจเอง
          </div>
        </div>
      );
    }
    return (
      <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
        ⏳ กำลังตรวจสลิปอัตโนมัติ...
      </div>
    );
  }
  return null;
}


export default function Inbox() {
  const [tab, setTab] = useState('open');
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  // slip_v1 state
  const [slipSettings, setSlipSettings] = useState({ mode: 'manual', has_token: false });
  const [slipQuota, setSlipQuota] = useState(null); // slip_quota_v1
  const [verifications, setVerifications] = useState({}); // { messageId: result }
  const [verifyingId, setVerifyingId] = useState(null);
  const [slipDetailModal, setSlipDetailModal] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // image_preview_v1
  const [messages, setMessages] = useState([]);
  const safeMessages = Array.isArray(messages) ? messages : [];
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [oldestMsgTime, setOldestMsgTime] = useState(null);
  const location = useLocation();
  const channelFilter = new URLSearchParams(location.search).get('channel');
  const [showCreateCase, setShowCreateCase] = useState(false);
  const [creatingCase, setCreatingCase] = useState(false); /* create_case_ui_v1: กันกดซ้ำ */
  /* unread_badge_v1: readMarks = เปิดอ่านเคสไหนล่าสุดเมื่อไหร่ (เก็บลง localStorage)
     liveUnread = นับข้อความลูกค้าที่เข้ามาทาง SSE หลังอ่านครั้งล่าสุด (อยู่ในหน่วยความจำพอ) */
  const [readMarks, setReadMarks] = useState(() => loadReadMarks());
  const [liveUnread, setLiveUnread] = useState({});
  const readMarksRef = useRef(readMarks);
  useEffect(() => { readMarksRef.current = readMarks; }, [readMarks]);
  // mobile_v1: เพิ่ม 'info' เป็น mobile view
  const [mobileView, setMobileView] = useState('list');  // 'list' | 'chat' | 'info'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // mobile_v1: action menu (3-dot menu) บนมือถือ
  const [showActionMenu, setShowActionMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [showCustomerPanel, setShowCustomerPanel] = useState(false);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name:'', tags:[], note:'' });
  const [newTag, setNewTag] = useState('');
  const [customers, setCustomers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [newCase, setNewCase] = useState({ customer_id:'', channel_id:'' });
  const [searchCustomer, setSearchCustomer] = useState('');
  const [reply, setReply] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrTag, setQrTag] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);
  // notify_sound_v1: เสียงเตือนข้อความใหม่ (ตั้งค่าเก็บใน localStorage ต่อผู้ใช้)
  const [soundEnabled, setSoundEnabled] = useState(() => getSoundSettings().enabled);
  const [soundType, setSoundType] = useState(() => getSoundSettings().sound);
  const [soundVolume, setSoundVolume] = useState(() => getSoundSettings().volume);
  const [showSoundMenu, setShowSoundMenu] = useState(false);
  const [desktopEnabled, setDesktopEnabled] = useState(() => getSoundSettings().desktop); // notify_desktop_v1
  const soundEnabledRef = useRef(soundEnabled);
  const soundTypeRef = useRef(soundType);
  const soundVolumeRef = useRef(soundVolume);
  const desktopEnabledRef = useRef(desktopEnabled);
  const convsRef = useRef([]); // notify_desktop_v1: ให้ SSE handler (ผูกครั้งเดียว) หาข้อมูล conversation ล่าสุดได้
  useEffect(() => { soundEnabledRef.current = soundEnabled; soundTypeRef.current = soundType; soundVolumeRef.current = soundVolume; desktopEnabledRef.current = desktopEnabled; }, [soundEnabled, soundType, soundVolume, desktopEnabled]);
  useEffect(() => { convsRef.current = convs; }, [convs]); // notify_desktop_v1
  const msgEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const agent = JSON.parse(localStorage.getItem('tenant') || '{}');

  /* conv_split_active_v1: closed loaded separately (history tab only) to keep hot path light */
  const [closedConvsData, setClosedConvsData] = useState([]);
  const loadClosedConvs = () => API.get('/api/conversations?status=closed').then(r => setClosedConvsData(r.data)).catch(()=>{});
  /* conv_share_v1: ดึงผ่านตัวกลาง — ถ้า Layout กำลังดึงอยู่พอดีจะเกาะ request เดียวกัน
     opts.minTime = "ต้องการข้อมูลที่เริ่มดึงหลังเวลานี้" (ดู lib/conversations.js) */
  const loadConvs = (opts) => fetchConversations(opts).then(data => {
    setConvs(data);
    /* case_status_sync_v1: keep selected in sync (status/handled_by/agent_name) when conv updates */
    setSelected(prev => {
      if (!prev) return prev;
      const fresh = data.find(c => String(c.id) === String(prev.id));
      if (!fresh) return prev;
      return { ...prev, status: fresh.status, handled_by: fresh.handled_by, agent_name: fresh.agent_name };
    });
  }).catch(() => {}); /* conv_share_v1: เดิมไม่มี catch — request ที่ fail กลายเป็น unhandled rejection */

  /* conv_share_v1: หลังกดปุ่มที่เปลี่ยนสถานะเคส ต้องได้ข้อมูลที่ดึงหลัง API ตอบแล้วเท่านั้น
     ห้ามเกาะ request ที่ยิงไปก่อนหน้า ไม่งั้นลิสต์จะยังโชว์สถานะเก่า */
  const reloadConvsFresh = () => loadConvs({ minTime: Date.now() });

  /* unread_badge_v1: เปิดอ่านเคสแล้ว -> จดเวลา + ล้างตัวนับ
     อ่าน readMarks จาก ref เพราะ SSE handler ผูกครั้งเดียว (closure ค้างตั้งแต่ render แรก) */
  const markConvRead = (convId, lastMessageAt) => {
    if (!convId) return;
    /* หมุดเวลาต้องไม่เก่ากว่าข้อความที่เพิ่งอ่าน — ถ้านาฬิกาเครื่องช้ากว่าเซิร์ฟเวอร์
       แล้วใช้ Date.now() เฉยๆ badge จะค้างแดงตลอดไปเพราะเทียบยังไงก็ไม่ผ่าน */
    const msgAt = lastMessageAt ? new Date(lastMessageAt).getTime() : 0;
    const at = Math.max(Date.now(), msgAt ? msgAt + 1 : 0);
    setReadMarks(saveReadMarks({ ...readMarksRef.current, [convId]: at }));
    setLiveUnread(prev => (prev[convId] ? { ...prev, [convId]: 0 } : prev));
  };
  const loadCreateCaseData = async () => {
    try { /* create_case_ui_v1: เดิมไม่มี catch — โหลดพังจะกลายเป็น unhandled rejection */
      const [cu, ch] = await Promise.all([API.get('/api/conversations/customers'), API.get('/api/channels')]);
      setCustomers(cu.data || []); setChannels(ch.data || []);
    } catch (e) { toast.error('โหลดรายชื่อลูกค้า/ช่องทางไม่สำเร็จ'); }
  };
  /* create_case_ui_v1: เปิดโมดัล + โหลดข้อมูลที่ต้องใช้ */
  const openCreateCase = () => {
    setNewCase({ customer_id:'', channel_id:'' });
    setSearchCustomer('');
    setShowCreateCase(true);
    loadCreateCaseData();
  };
  const closeCreateCase = () => {
    setShowCreateCase(false);
    setNewCase({ customer_id:'', channel_id:'' });
    setSearchCustomer('');
  };
  const handleCreateCase = async () => {
    if (!newCase.customer_id || !newCase.channel_id) { toast.error('กรุณาเลือกลูกค้าและช่องทาง'); return; }
    if (creatingCase) return; /* create_case_ui_v1: กันกดรัว */
    setCreatingCase(true);
    try {
      const res = await API.post('/api/conversations/create', newCase);
      closeCreateCase();
      await reloadConvsFresh();
      /* create_case_ui_v1: เปิดเคสที่เพิ่งสร้างให้เลย จะได้พิมพ์ต่อได้ทันที */
      const created = res && res.data;
      if (created && created.id) {
        setTab('open');
        setSelected(created);
        markConvRead(created.id);
        if (isMobile) setMobileView('chat');
      }
      toast.success('เปิดเคสใหม่แล้ว');
    } catch(err) { toast.error(err.response?.data?.error || 'เปิดเคสไม่สำเร็จ'); }
    setCreatingCase(false);
  };
  /* history_keep_v1: จำว่าเคสนี้ผู้ใช้กด "โหลดข้อความเก่า" ไปแล้วหรือยัง
     ถ้าโหลดแล้ว การ refresh (poll 30s / หลังส่ง / SSE) ต้อง "merge" ไม่ใช่ "replace"
     ไม่งั้นประวัติที่โหลดมาหายกลับไปเหลือ 50 ข้อความล่าสุด */
  const paginatedRef = useRef(false);

  // opts.merge = true สำหรับการ refresh (ไม่ใช่การเปิดเคสครั้งแรก)
  const loadMsgs = (id, opts = {}) => {
    const isRefresh = !!opts.merge;
    const doMerge = isRefresh && paginatedRef.current;
    API.get(`/api/conversations/${id}/all-messages?limit=50&_=${Date.now()}`).then(r => {
      const fresh = (r.data && Array.isArray(r.data.messages)) ? r.data.messages
                  : Array.isArray(r.data) ? r.data
                  : null;
      if (fresh) {
        if (doMerge) {
          /* history_keep_v1: ต่อท้ายเฉพาะข้อความที่ยังไม่มี — คงลำดับเดิมไว้ (ไม่ sort ใหม่)
             เพื่อไม่ให้เส้นคั่นบทสนทนา (conv_id divider) เพี้ยน
             และไม่แตะ hasMore/oldest เพราะประวัติเก่ายังอยู่ครบ */
          setMessages(prev => {
            const seen = new Set(prev.map(m => String(m.id)));
            const added = fresh.filter(m => !seen.has(String(m.id)));
            return added.length ? [...prev, ...added] : prev;
          });
        } else {
          setMessages(fresh);
          setHasMoreMessages((r.data && r.data.hasMore) || false);
          setOldestMsgTime((r.data && r.data.oldest) || null);
        }
      }

      // ส่วนที่แก้: เช็กก่อนเลื่อนลง
      if (!userScrolledUpRef.current) {
        setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
      }
    }).catch(() => {
      /* history_keep_v1: poll ที่ fail ชั่วคราวต้องไม่ล้างแชททิ้งทั้งห้อง */
      if (!isRefresh) setMessages([]);
    }); // ปิด .then ตรงนี้

    if (isMobile) setMobileView('chat');
  }; // จบฟังก์ชัน loadMsgs ตรงนี้

  const loadMoreMsgs = async () => {
    if (!selected || !hasMoreMessages || loadingMore || !oldestMsgTime) return;
    setLoadingMore(true);
    isLoadingMoreRef.current = true;
    try {
      const { data } = await API.get(`/api/conversations/${selected.id}/all-messages?limit=50&before=${oldestMsgTime}&_=${Date.now()}`);
      setMessages(prev => [...data.messages, ...prev]);
      setHasMoreMessages(data.hasMore);
      setOldestMsgTime(data.oldest);
      paginatedRef.current = true; /* history_keep_v1 */
    } catch {
      isLoadingMoreRef.current = false; /* history_keep_v1: ถ้าโหลดไม่สำเร็จ ต้องปลด flag เอง (messages ไม่เปลี่ยน effect จึงไม่ทำงาน) */
    }
    setLoadingMore(false);
  };
  const loadQR = () => API.get('/api/quickreplies').then(r => setQuickReplies(r.data));

  useEffect(() => { loadConvs(); loadQR(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  /* poll_adaptive_v1: SSE ต่ออยู่ = event มาเอง poll แค่กันเหนียว (90 วิ) / SSE หลุด = กลับไป 30 วิ */
  useAdaptivePoll(loadConvs, { fastMs: 30000, slowMs: 90000 });

  /* unread_badge_v1: ครั้งแรกที่ใช้ฟีเจอร์นี้ (ยังไม่มีหมุดสักอัน) ให้ถือว่าแชทที่มีอยู่แล้ว "อ่านแล้ว"
     ไม่งั้นวันที่อัพขึ้นเซิร์ฟ ทุกเคสในลิสต์จะเด้งแดงพร้อมกันหมดทั้งที่แอดมินอ่านไปแล้ว
     หลังจากนี้เฉพาะข้อความที่เข้ามาใหม่จริงๆ ถึงจะขึ้นเตือน */
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !convs.length) return;
    seededRef.current = true;
    if (Object.keys(readMarksRef.current).length > 0) return; // เคยใช้แล้ว อย่าไปทับของเดิม
    const now = Date.now();
    const seed = {};
    convs.forEach(c => {
      if (!c.last_message_at) return;
      const msgAt = new Date(c.last_message_at).getTime();
      seed[c.id] = Math.max(now, msgAt ? msgAt + 1 : 0);
    });
    if (Object.keys(seed).length) setReadMarks(saveReadMarks(seed));
  }, [convs]);

  // sse_client_v1: real-time SSE connection
  const selectedIdRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selected?.id || null; }, [selected?.id]);

  // slip_v1: load settings on mount
  useEffect(() => {
    API.get('/api/slip-verify/settings').then(r => setSlipSettings(r.data)).catch(() => {});
    API.get('/api/slip-verify/quota').then(r => setSlipQuota(r.data)).catch(() => {}); // slip_quota_v1
  }, []);

  // slip_quota_v1: helper reload quota
  const reloadQuota = () => {
    API.get('/api/slip-verify/quota').then(r => setSlipQuota(r.data)).catch(() => {});
  };

  // slip_v1: load verifications สำหรับ messages ที่เป็นรูปลูกค้า
  /* slip_retry_v1: เดิมถามครั้งเดียวต่อรูป ถ้ายังไม่มีผลจะจำว่า "ไม่มี" แล้วไม่ลองใหม่อีกเลย
     โหมด auto จึงค้างข้อความ "กำลังตรวจสลิปอัตโนมัติ..." ตลอดไปถ้าพลาด SSE slip_verified
     (เช่นเน็ตสะดุดตอนผลออกพอดี) ตอนนี้ลองใหม่ได้จำนวนจำกัด แล้วถ้ายังไม่ได้จริงๆ
     เปลี่ยนเป็นปุ่มให้กดตรวจเอง — ดีกว่าหมุนหลอกไปเรื่อยๆ */
  const isSlipCandidate = (m) => m.sender_type === 'customer' && m.file_type === 'image' && m.file_url;
  const slipAttemptsRef = useRef({});
  const [slipStalled, setSlipStalled] = useState({});
  const [slipTick, setSlipTick] = useState(0);

  useEffect(() => {
    if (!safeMessages.length) return;
    safeMessages.forEach(m => {
      if (!isSlipCandidate(m)) return;
      if (verifications[m.id]) return;                     // ได้ผลแล้ว จบ
      const attempts = slipAttemptsRef.current[m.id] || 0;
      if (attempts >= SLIP_MAX_ATTEMPTS) return;           // ลองครบแล้ว
      if (attempts > 0 && slipSettings.mode !== 'auto') return; // manual: ถามครั้งเดียวพอ ไม่ต้องวน

      const next = attempts + 1;
      slipAttemptsRef.current[m.id] = next;
      const giveUp = () => {
        if (next >= SLIP_MAX_ATTEMPTS) setSlipStalled(prev => ({ ...prev, [m.id]: true }));
      };
      API.get(`/api/slip-verify/${m.id}`)
        .then(r => { if (r.data) setVerifications(prev => ({ ...prev, [m.id]: r.data })); else giveUp(); })
        .catch(giveUp);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, slipTick]);

  /* slip_retry_v1: เดินนาฬิกาเฉพาะตอนที่ยังมีรูปรอผลอยู่จริง — ไม่มีของค้างก็ไม่ต้องเดิน */
  const hasPendingSlip = slipSettings.mode === 'auto' && safeMessages.some(m =>
    isSlipCandidate(m) && !verifications[m.id] && (slipAttemptsRef.current[m.id] || 0) < SLIP_MAX_ATTEMPTS
  );
  useEffect(() => {
    if (!hasPendingSlip) return undefined;
    const t = setTimeout(() => setSlipTick(x => x + 1), SLIP_RETRY_INTERVAL);
    return () => clearTimeout(t);
  }, [hasPendingSlip, slipTick]);

  // slip_v1: ตรวจสลิป (manual)
  const verifySlip = async (messageId) => {
    setVerifyingId(messageId);
    try {
      const r = await API.post(`/api/slip-verify/${messageId}`);
      setVerifications(prev => ({ ...prev, [messageId]: r.data }));
      setSlipDetailModal(r.data);
      reloadQuota(); // slip_quota_v1
    } catch (e) {
      // slip_quota_v1: handle quota exceeded
      if (e.response?.data?.quota_exceeded) {
        reloadQuota();
        alert('⛔ ' + e.response.data.error);
      } else {
        alert(e.response?.data?.error || e.message);
      }
    }
    setVerifyingId(null);
  };
  const lastNewMessageAt = useRef(0); // realtime_v2: debounce conversation_updated
  const loadConvsTimer = useRef(null); /* debounce_v1: coalesce SSE-triggered loadConvs to cut query burst */
  /* conv_share_v1: จำเวลาที่ event มาถึง แล้วส่งเป็น minTime
     กันไม่ให้ไปเกาะ request ที่ยิงไปก่อนหน้า event นี้ (ซึ่งยังไม่มีข้อมูลใหม่)
     แต่ยังเกาะกับ Layout ที่ได้ event เดียวกันในจังหวะเดียวกันได้ */
  const debouncedLoadConvs = () => {
    const at = Date.now();
    clearTimeout(loadConvsTimer.current);
    loadConvsTimer.current = setTimeout(() => loadConvs({ minTime: at }), 400);
  };


  useEffect(() => {
    const conn = connectSSE({
      /* realtime_recover_v1: re-sync ทุกครั้งที่ connect/reconnect
         poll_adaptive_v1: ต้อง re-sync "ข้อความ" ของเคสที่เปิดอยู่ด้วย เพราะ poll ถูกยืดออกไป
         ตอน SSE ปกติ — ถ้าเพิ่งกลับมาจากช่วงหลุด ข้อความที่พลาดไปต้องตามกลับมาทันที */
      onConnect: (info) => {
        loadConvs({ minTime: (info && info.at) || Date.now() }); /* conv_share_v1: ใช้เวลาเดียวกับ Layout */
        if (selectedIdRef.current) loadMsgs(selectedIdRef.current, { merge: true });
      },
      // realtime_v1: append message ทันที — ไม่ต้อง refetch (เร็ว < 200ms)
      onNewMessage: (data) => {
        lastNewMessageAt.current = Date.now();
        const msg = data.message;
        const convId = data.conversationId;
        // notify_sound_v1: เล่นเสียงเตือนเมื่อลูกค้าส่งข้อความใหม่ (อ่านค่าจาก ref เพราะ handler ผูกครั้งเดียว)
        if (msg && msg.sender_type === 'customer' && soundEnabledRef.current) {
          try { playNotifySound(soundTypeRef.current, soundVolumeRef.current); } catch (e) {}
        }
        // notify_desktop_v1: เด้งแจ้งเตือนมุมจอทุกครั้งที่ลูกค้าส่งข้อความใหม่ (ไม่ว่าจะดูแท็บนี้อยู่หรือไม่)
        if (msg && msg.sender_type === 'customer' && desktopEnabledRef.current) {
          const conv = convsRef.current.find(c => String(c.id) === String(convId));
          try {
            showDesktopNotification({
              title: '💬 ' + ((conv && conv.customer_name) || 'ลูกค้าใหม่') + (conv && conv.channel_name ? ' · ' + conv.channel_name : ''),
              body: msg.content || (msg.file_type === 'image' ? '[รูปภาพ]' : '[ไฟล์]'),
              icon: conv && conv.customer_profile_pic,
              tag: 'conv-' + convId,
              onClick: () => { if (conv) { setSelected(conv); if (isMobile) setMobileView('chat'); } },
            });
          } catch (e) {}
        }
        if (String(selectedIdRef.current) === String(convId)) {
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          /* unread_badge_v1: กำลังเปิดดูเคสนี้อยู่ = อ่านแล้ว เลื่อนหมุดเวลาอ่านตามไปด้วย */
          markConvRead(convId, msg && msg.created_at);
        } else if (msg && msg.sender_type === 'customer') {
          /* unread_badge_v1: ไม่ได้เปิดดูอยู่ -> นับเพิ่มไว้โชว์บน badge */
          setLiveUnread(prev => ({ ...prev, [convId]: (prev[convId] || 0) + 1 }));
        }
        setConvs(prev => {
          // newconv_v1: ถ้า conversation ใหม่ (ไม่มีใน list) → reload ทันที
          if (!prev.some(c => String(c.id) === String(convId))) {
            setTimeout(loadConvs, 0);
            return prev;
          }
          return prev.map(c => {
          if (String(c.id) !== String(convId)) return c;
          /* unread_badge_v1: เลิกสะสม unread_count ตรงนี้แล้ว — ไม่มีใครอ่านค่านี้ และ loadConvs
             ก็ทับทิ้งทุกรอบอยู่ดี ตอนนี้ย้ายไปนับที่ liveUnread + readMarks แทน */
          return {
            ...c,
            last_message: msg.content || (msg.file_type === 'image' ? '[image]' : '[file]'),
            last_message_at: msg.created_at,
            updated_at: msg.created_at,
          };
        });
        });
      },
      onConversationUpdated: (data) => {
        // newconv_v1 + debounce_call_1: coalesce rapid SSE events
        debouncedLoadConvs();
        // realtime_v2: skip loadMsgs ถ้าเพิ่งได้ new_message (debounce 2s)
        if (Date.now() - lastNewMessageAt.current < 2000) return;
        if (data && data.conversationId && String(data.conversationId) === String(selectedIdRef.current)) {
          loadMsgs(selectedIdRef.current, { merge: true }); /* history_keep_v1 */
        }
      },
      // slip_v1: re-fetch verification เมื่อ auto-verify เสร็จ
      onSlipVerified: (data) => {
        if (data && data.messageId) {
          API.get(`/api/slip-verify/${data.messageId}`)
            .then(r => setVerifications(prev => ({ ...prev, [data.messageId]: r.data })))
            .catch(() => {});
          reloadQuota(); // slip_quota_v1
        }
      },
      // slip_quota_v1: quota หมด — popup
      onSlipQuotaFull: (data) => {
        reloadQuota();
        alert(`⛔ โควต้าตรวจสลิปหมดแล้ว (${data.used}/${data.total} ครั้ง)\n\nกรุณาติดต่อแอดมินเพื่อขอเพิ่มเครดิต`);
      }
    });
    return () => conn.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* esc_listener_v1: ปิดรูปที่เปิดดูเต็มจอด้วยปุ่ม ESC
     ผูกตอนเปิดรูป ถอดตอนปิด/ออกจากหน้า — React จัดการอายุของ listener ให้เอง
     (ของเดิมผูกระหว่าง render แล้วเก็บ handler ไว้บน window.__imgPreviewEsc
      ถ้าออกจากหน้านี้ตอนเปิดรูปค้างอยู่ listener จะค้างบน window ไม่มีใครถอดให้) */
  useEffect(() => {
    if (!imagePreview) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setImagePreview(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [imagePreview]);

  /* dead_code_v1: ตรงนี้เคยมี useEffect ผูก scroll listener ให้ chatContainerRef
     แต่มันไม่เคยทำงานเลย — deps เป็น [] จึงรันตอน mount ครั้งเดียว ซึ่งตอนนั้นยังไม่ได้เลือกแชท
     กล่องข้อความยังไม่ถูก render ref เลยเป็น null แล้ว return ทิ้งไปเฉยๆ ไม่มีการผูกใหม่อีก
     ตัวที่ทำงานจริงมาตลอดคือ onScroll ที่ใส่ไว้บน element โดยตรง (ดูตรง Messages) */

  useEffect(() => {
    /* staleClosureFix_v2 */
    if (!selected?.id) {
      setMessages([]);
      return;
    }
    const id = selected.id;
    paginatedRef.current = false; /* history_keep_v1: เปิดเคสใหม่ = เริ่มนับใหม่ */
    loadMsgs(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  /* poll_adaptive_v1: ตาข่ายกันพลาดของ "ข้อความ" — อ่าน id จาก ref จึงไม่ต้อง restart timer ตอนสลับเคส
     ปกติข้อความมาทาง SSE new_message อยู่แล้ว และ onConnect ก็ดึงซ้ำให้ตอนกลับมาจากช่วงหลุด */
  useAdaptivePoll(
    () => { if (selectedIdRef.current) loadMsgs(selectedIdRef.current, { merge: true }); },
    { fastMs: 30000, slowMs: 90000, enabled: !!selected?.id }
  );

  useEffect(() => {
    // 1. ถ้ากำลังโหลดข้อความเก่า (Pagination) ห้ามเลื่อนลง
    if (isLoadingMoreRef.current) {
      isLoadingMoreRef.current = false;
      return;
    }

    // 2. ถ้า User ไม่ได้เลื่อนขึ้น (false) ให้เลื่อนลงล่างสุด
    if (!userScrolledUpRef.current) {
      msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendReply = async () => {
    if (!reply.trim() && !pendingFile) return;
    try {
      await API.post(`/api/conversations/${selected.id}/reply`, {
        content: reply,
        agent_id: agent.id,
        file_url: pendingFile?.url || null,
        file_type: pendingFile?.type || null,
      });
      setReply(''); setPendingFile(null); setShowQR(false); loadMsgs(selected.id, { merge: true }); /* history_keep_v1 */
    } catch { toast.error('ส่งไม่สำเร็จ'); }
  };

  const handleFileUpload = async (eOrFile) => { /* image_paste_v1: accepts event OR File */
    const e = eOrFile?.target ? eOrFile : { target: { files: [eOrFile] } };
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await API.post('/api/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPendingFile({ url: data.url, type: data.type, name: data.name });
      toast.success('อัปโหลดสำเร็จ พร้อมส่ง');
    } catch { toast.error('อัปโหลดไม่สำเร็จ'); }
    setUploading(false);
    e.target.value = '';
  };

  const loadCustomerProfile = async (customerId) => {
    try {
      const { data } = await API.get(`/api/conversations/customers/${customerId}`);
      setCustomerProfile(data);
      setCustomerForm({ name: data.name || '', tags: data.tags || [], note: data.note || '' });
    } catch {}
  };

  const saveCustomerProfile = async () => {
    try {
      await API.patch(`/api/conversations/customers/${customerProfile.id}`, customerForm);
      setCustomerProfile({ ...customerProfile, ...customerForm });
      setEditingCustomer(false);
      reloadConvsFresh();
    } catch { toast.error('บันทึกไม่สำเร็จ'); }
  };


  const acceptCase = async (id) => {
    try {
      await API.patch(`/api/conversations/${id}/accept`, {});
      setSelected(prev => prev?.id === id ? { ...prev, handled_by: 'agent' } : prev);
      /* persistentAlert_v1: แจ้ง Layout ให้หยุดเสียงทันที (ไม่ต้องรอ poll 10s) */
      window.dispatchEvent(new CustomEvent('case-accepted', { detail: { id } }));
      reloadConvsFresh();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const closeConv = async (id) => { await API.patch(`/api/conversations/${id}/close`, { agent_id: agent.id }); window.dispatchEvent(new CustomEvent('case-accepted', { detail: { id } })); reloadConvsFresh(); if (selected?.id === id) setSelected(null); toast.success('ปิดเคสแล้ว'); setShowActionMenu(false); };
  const blockConv = async (conv) => { await API.patch(`/api/conversations/${conv.id}/block`); window.dispatchEvent(new CustomEvent('case-accepted', { detail: { id: conv.id } })); reloadConvsFresh(); if (selected?.id === conv.id) setSelected(null); toast.success('บล็อคแล้ว'); setShowActionMenu(false); };
  const unblockConv = async (conv) => { await API.patch(`/api/conversations/${conv.id}/unblock`); reloadConvsFresh(); toast.success('ยกเลิกบล็อคแล้ว'); setShowActionMenu(false); };
  // reopen_v1: สร้างเคสใหม่จากเคสที่ปิดแล้ว (preserves KPI accuracy)
  const reopenConv = async (oldConvId) => {
    try {
      const res = await API.post(`/api/conversations/${oldConvId}/reopen`);
      const newConv = res.data;
      toast.success('เปิดเคสใหม่สำเร็จ');
      await reloadConvsFresh();
      setTab('open');
      setSelected(newConv);
      setShowActionMenu(false);
    } catch (err) {
      const msg = err?.response?.data?.error || 'เปิดเคสใหม่ไม่สำเร็จ';
      toast.error(msg);
      setShowActionMenu(false);
    }
  };

  // mobile_v1: handler สำหรับเปิดโปรไฟล์ลูกค้า
  const openCustomerInfo = () => {
    if (selected?.customer_id) loadCustomerProfile(selected.customer_id);
    if (isMobile) {
      setMobileView('info');
    } else {
      setShowCustomerPanel(true);
    }
  };

  const openConvs = convs.filter(c => c.status !== 'closed' && c.status !== 'blocked' && (!channelFilter || c.channel_id === channelFilter));
  const closedConvs = closedConvsData.filter(c => (!channelFilter || c.channel_id === channelFilter)); /* conv_split_2b: from separate closed state */
  const blockedConvs = convs.filter(c => c.status === 'blocked' && (!channelFilter || c.channel_id === channelFilter)); /* channel_filter_history_v1 */
  const displayConvs = tab === 'open' ? openConvs : tab === 'closed' ? closedConvs : blockedConvs;
  const TYPE_ICONS = { line: '💚', telegram: '✈️', facebook: '💙', whatsapp: '💬', webchat: '🌐' };
  const tabs = [
    { key: 'open', label: 'แชทใหม่', icon: MessageSquare, count: openConvs.length },
    { key: 'closed', label: 'ประวัติ', icon: Clock, count: closedConvs.length },
    { key: 'blocked', label: 'บล็อค', icon: Ban, count: blockedConvs.length },
  ];
  /* qr_business_v1: กรองข้อความด่วนตามธุรกิจของเคสที่เปิดอยู่ (channel_tag) + ส่วนกลาง(business ว่าง) */
  const convBiz = selected?.channel_tag || null;
  const qrByBiz = quickReplies.filter(r => !r.business || r.business === convBiz);
  const qrTags = ['all', ...new Set(qrByBiz.map(r => r.tag))];
  const filteredQR = qrTag === 'all' ? qrByBiz : qrByBiz.filter(r => r.tag === qrTag);
  const tagColors = ['#0ea5e9','#22c55e','#f59e0b','#ef4444','#a855f7','#ec4899','#14b8a6','#f97316'];
  const getTagColor = (tag) => tagColors[Math.abs((tag||'').split('').reduce((a,b) => a + b.charCodeAt(0), 0)) % tagColors.length];

  // mobile_v1: ตัดสินใจว่าจะแสดง panel ไหนบน mobile
  // บน desktop แสดงทั้งหมด, บน mobile แสดงตาม mobileView
  const showList = !isMobile || mobileView === 'list';
  const showChat = !isMobile || mobileView === 'chat';
  const showInfoMobile = isMobile && mobileView === 'info';

  return (
    <div style={{
      display: 'flex',
      // mobile_v1: ใช้ 100% ของ parent — Layout main คำนวณ height (100vh - 64px) ให้แล้ว
      height: isMobile ? '100%' : 'calc(100vh - 48px)',
      borderRadius: isMobile ? 0 : 12,
      overflow: 'hidden',
      boxShadow: isMobile ? 'none' : '0 1px 8px rgba(0,0,0,0.08)',
      border: isMobile ? 'none' : '1px solid #e2e8f0'
    }}>

      {/* Left: Conversation List */}
      {showList && (
        <div style={{
          width: isMobile ? '100%' : 300,
          background: '#fff',
          display: 'flex', flexDirection: 'column',
          borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
          height: '100%',
        }}>
          {/* Header */}
          <div style={{
            padding: isMobile ? '14px 16px 10px' : '16px',
            borderBottom: '1px solid #e2e8f0',
            background: isMobile ? '#fff' : '#fff',
            position: isMobile ? 'sticky' : 'static',
            top: 0, zIndex: 5,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, position: 'relative' }}>
              <h3 style={{ fontSize: isMobile ? 20 : 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                💬 กล่องข้อความ
              </h3>
              {/* create_case_ui_v1: เปิดเคสใหม่หาลูกค้าเอง */}
              <button title="เปิดเคสใหม่" onClick={openCreateCase}
                style={{ background: '#f0f9ff', color: '#0ea5e9', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                <Plus size={17} />
              </button>
              {/* notify_sound_v1: ปุ่มตั้งค่าเสียงเตือนข้อความใหม่ */}
              <button title="เสียงเตือนข้อความใหม่" onClick={() => { unlockAudio(); setShowSoundMenu(s => !s); }}
                style={{ background: soundEnabled ? '#e0f2fe' : '#f1f5f9', color: soundEnabled ? '#0ea5e9' : '#94a3b8', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                {soundEnabled ? <Bell size={17} /> : <BellOff size={17} />}
              </button>
              {showSoundMenu && (
                <>
                  <div onClick={() => setShowSoundMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{ position: 'absolute', top: '112%', right: 0, width: 258, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.14)', padding: 14, zIndex: 41 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>🔔 เสียงเตือนข้อความใหม่</span>
                      <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 22, flex: '0 0 auto', cursor: 'pointer' }}>
                        <input type="checkbox" checked={soundEnabled}
                          onChange={e => { const v = e.target.checked; setSoundEnabled(v); setSoundSettings({ enabled: v }); if (v) { unlockAudio(); playNotifySound(soundType, soundVolume); } }}
                          style={{ opacity: 0, width: 0, height: 0 }} />
                        <span style={{ position: 'absolute', inset: 0, background: soundEnabled ? '#0ea5e9' : '#cbd5e1', borderRadius: 22, transition: '0.2s' }} />
                        <span style={{ position: 'absolute', top: 2, left: soundEnabled ? 20 : 2, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: '0.2s' }} />
                      </label>
                    </div>
                    <div style={{ opacity: soundEnabled ? 1 : 0.45, pointerEvents: soundEnabled ? 'auto' : 'none' }}>
                      <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>เลือกเสียง (คลิกเพื่อฟัง)</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                        {SOUND_OPTIONS.map(o => (
                          <button key={o.value} onClick={() => { setSoundType(o.value); setSoundSettings({ sound: o.value }); unlockAudio(); playNotifySound(o.value, soundVolume); }}
                            style={{ padding: '6px 10px', borderRadius: 8, border: soundType === o.value ? '2px solid #0ea5e9' : '1px solid #e2e8f0', background: soundType === o.value ? '#e0f2fe' : '#fff', color: '#334155', fontSize: 12, cursor: 'pointer', fontWeight: soundType === o.value ? 700 : 500 }}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                      <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}><Volume2 size={14} /> ความดัง</label>
                      <input type="range" min="0" max="1" step="0.1" value={soundVolume}
                        onChange={e => { const v = parseFloat(e.target.value); setSoundVolume(v); setSoundSettings({ volume: v }); }}
                        onMouseUp={e => { unlockAudio(); playNotifySound(soundType, parseFloat(e.target.value)); }}
                        style={{ width: '100%' }} />
                    </div>
                    {/* notify_desktop_v1: แจ้งเตือนหน้าจอตอนสลับแท็บ */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>🖥️ แจ้งเตือนหน้าจอ
                        <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', fontWeight: 400, marginTop: 2 }}>เด้งมุมจอทุกครั้งที่มีข้อความใหม่</span>
                      </span>
                      <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 22, flex: '0 0 auto', cursor: notifySupported() ? 'pointer' : 'not-allowed' }}>
                        <input type="checkbox" checked={desktopEnabled} disabled={!notifySupported()}
                          onChange={async e => {
                            const v = e.target.checked;
                            if (v) {
                              const perm = await requestNotifyPermission();
                              if (perm !== 'granted') { toast.error(perm === 'denied' ? 'เบราว์เซอร์บล็อกการแจ้งเตือน — เปิดสิทธิ์ในตั้งค่าเบราว์เซอร์ก่อน' : 'เบราว์เซอร์นี้ไม่รองรับการแจ้งเตือน'); return; }
                              showDesktopNotification({ title: '✅ เปิดแจ้งเตือนหน้าจอแล้ว', body: 'จะเด้งเตือนทุกครั้งที่มีข้อความใหม่', tag: 'notify-test' });
                            }
                            setDesktopEnabled(v); setSoundSettings({ desktop: v });
                          }}
                          style={{ opacity: 0, width: 0, height: 0 }} />
                        <span style={{ position: 'absolute', inset: 0, background: desktopEnabled ? '#0ea5e9' : '#cbd5e1', borderRadius: 22, transition: '0.2s' }} />
                        <span style={{ position: 'absolute', top: 2, left: desktopEnabled ? 20 : 2, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: '0.2s' }} />
                      </label>
                    </div>
                    {desktopEnabled && notifyPermission() !== 'granted' && (
                      <p style={{ fontSize: 11, color: '#ef4444', margin: '6px 0 0' }}>⚠️ ยังไม่ได้อนุญาตสิทธิ์แจ้งเตือนในเบราว์เซอร์</p>
                    )}
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '10px 0 0' }}>ตั้งค่าเฉพาะเครื่อง/ผู้ใช้นี้</p>
                  </div>
                </>
              )}
            </div>
            {isMobile && openConvs.length > 0 && (
              <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
                {openConvs.length} รายการ
              </p>
            )}
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            background: '#fff',
          }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); if (t.key === 'closed') loadClosedConvs(); }}
                style={{
                  flex: '0 0 auto',
                  minWidth: isMobile ? 100 : 80,
                  padding: isMobile ? '14px 12px' : '10px 8px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: tab === t.key ? '#0ea5e9' : '#94a3b8',
                  fontWeight: tab === t.key ? 700 : 500,
                  borderBottom: tab === t.key ? '2px solid #0ea5e9' : '2px solid transparent',
                  fontSize: isMobile ? 14 : 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 6, whiteSpace: 'nowrap',
                }}>
                <t.icon size={isMobile ? 16 : 12} />
                {t.label}
                {t.count > 0 && (
                  <span style={{
                    background: tab === t.key ? '#0ea5e9' : '#e2e8f0',
                    color: tab === t.key ? '#fff' : '#64748b',
                    borderRadius: 10,
                    padding: isMobile ? '2px 8px' : '1px 6px',
                    fontSize: isMobile ? 11 : 10, fontWeight: 600,
                  }}>{t.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {displayConvs.length === 0 && (
              <p style={{ color: '#94a3b8', textAlign: 'center', padding: 32, fontSize: 13 }}>ไม่มีรายการ</p>
            )}
            {displayConvs.map(c => {
              const unread = unreadStatus(c, readMarks, liveUnread); /* unread_badge_v1 */
              return (
              <div key={c.id} onClick={() => { setSelected(c); markConvRead(c.id, c.last_message_at); setShowQR(false); setPendingFile(null); setReply(''); if (isMobile) setMobileView('chat'); }}
                style={{
                  padding: isMobile ? '14px 16px' : '12px 16px',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer',
                  background: (!isMobile && selected?.id === c.id) ? '#f0f9ff' : '#fff',
                  borderLeft: (!isMobile && selected?.id === c.id) ? '3px solid ' + (c.channel_color || '#0ea5e9') : '3px solid transparent',
                  // mobile: tap feedback ผ่าน :active ใน CSS ไม่ได้ ใช้ transition แทน
                  transition: 'background 0.15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 10 }}>
                  {/* avatar_v1: รูปลูกค้า + fallback initial */}
                  <Avatar
                    name={c.customer_name}
                    pic={c.customer_profile_pic}
                    size={isMobile ? 48 : 40}
                    color={c.channel_color}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 3 : 0 }}>
                      <span style={{ fontWeight: 600, fontSize: isMobile ? 15 : 14, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.customer_name || 'Unknown'}
                      </span>
                      {c.last_message_at && (() => {
                        /* conv_list_smart_time_v1: BKK timezone, today → HH:MM, other → DD/MM/YY + HH:MM (stacked) */
                        const d = new Date(c.last_message_at);
                        const bkkOpts = { timeZone: 'Asia/Bangkok' };
                        const dDateBkk = d.toLocaleDateString('en-CA', bkkOpts); /* YYYY-MM-DD for compare */
                        const todayBkk = new Date().toLocaleDateString('en-CA', bkkOpts);
                        const isToday = dDateBkk === todayBkk;
                        const time = d.toLocaleTimeString('th-TH', { ...bkkOpts, hour: '2-digit', minute: '2-digit' });
                        const date = d.toLocaleDateString('th-TH', { ...bkkOpts, day: '2-digit', month: '2-digit', year: '2-digit' });
                        return (
                          <span style={{ fontSize: isMobile ? 11 : 10, color: '#94a3b8', flexShrink: 0, marginLeft: 6, textAlign: 'right', lineHeight: 1.3 }}>
                            {isToday ? time : (<><div>{date}</div><div>{time}</div></>)}
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap', marginBottom: isMobile ? 4 : 2 }}>
                      <span style={{ fontSize: isMobile ? 11 : 10, color: '#fff', background: c.channel_color || '#0ea5e9', padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>
                        {c.channel_name || c.channel_type}
                      </span>
                      {c.channel_tag && (
                        <span style={{ fontSize: isMobile ? 11 : 10, color:'#92400e', background:'#fef3c7', padding:'2px 7px', borderRadius:4, fontWeight:600 }}>
                          📁 {c.channel_tag}
                        </span>
                      )}
                    </div>
                    {/* unread_badge_v1: ข้อความล่าสุด + ป้ายยังไม่อ่านทางขวา */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <p style={{
                        color: unread.unread ? '#475569' : '#94a3b8',
                        fontWeight: unread.unread ? 600 : 400,
                        fontSize: isMobile ? 13 : 12,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        margin: 0, flex: 1, minWidth: 0,
                      }}>
                        {c.last_message && c.last_message.length > 50 ? c.last_message.substring(0,50)+'...' : c.last_message || '...'}
                      </p>
                      {unread.unread && (
                        unread.count > 0 ? (
                          /* รู้จำนวนแน่ชัด (นับจาก SSE หลังอ่านครั้งล่าสุด) */
                          <span style={{
                            background: '#ef4444', color: '#fff', borderRadius: 10,
                            minWidth: isMobile ? 20 : 18, height: isMobile ? 20 : 18,
                            padding: '0 6px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: isMobile ? 11 : 10, fontWeight: 700,
                          }}>
                            {unread.count > 99 ? '99+' : unread.count}
                          </span>
                        ) : (
                          /* รู้แค่ว่ามีของใหม่ แต่ไม่รู้จำนวน (เช่นเพิ่งรีเฟรชหน้า) -> จุดแดง */
                          <span title="มีข้อความใหม่ที่ยังไม่ได้อ่าน" style={{
                            background: '#ef4444', borderRadius: '50%',
                            width: 9, height: 9, flexShrink: 0,
                          }} />
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Right: Chat or Empty */}
      {showChat && (
        <div style={{
          flex: 1,
          background: '#f8fafc',
          display: 'flex', flexDirection: 'column',
          width: isMobile ? '100%' : 'auto',
          height: '100%',
        }}>
          {selected ? (
            <>
              {/* Header — mobile_v2: ลบ position sticky (บัค iOS) ใช้ flex layout มั่นคงกว่า */}
              <div style={{
                padding: isMobile ? '10px 8px 10px 4px' : '8px 10px',
                background: '#fff',
                borderBottom: isMobile ? '1px solid #e2e8f0' : '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                gap: 8,
                minHeight: isMobile ? 60 : 56,
                boxShadow: isMobile ? '0 1px 3px rgba(0,0,0,0.04)' : 'none',
                zIndex: 10,
              }}>
                {/* ปุ่มกลับ — mobile โชว์เป็น ChevronLeft, desktop คงเดิม */}
                {isMobile && (
                  <button onClick={() => { setMobileView('list'); }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      width: 40, height: 40, borderRadius: 20,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#1e293b', flexShrink: 0,
                    }}>
                    <ChevronLeft size={26} />
                  </button>
                )}

                {/* Customer info — กดเพื่อเปิดโปรไฟล์ */}
                <div onClick={openCustomerInfo}
                  style={{
                    display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 10,
                    flex: 1, minWidth: 0, cursor: 'pointer',
                  }}>
                  {/* Avatar with online indicator */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    {/* avatar_v1: รูปลูกค้าใน header */}
                    <Avatar
                      name={selected.customer_name}
                      pic={selected.customer_profile_pic}
                      size={isMobile ? 44 : 38}
                      color={selected.channel_color}
                      fontSize={isMobile ? 18 : 16}
                    />
                    {/* mobile_v2: ช่องทางสีฟ้า/เขียว (LINE = เขียว) มุมล่างขวา */}
                    {isMobile && (
                      <div style={{
                        position: 'absolute', bottom: -2, right: -2,
                        width: 16, height: 16, borderRadius: '50%',
                        background: selected.channel_color || '#0ea5e9',
                        border: '2px solid #fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9,
                      }}>
                        {TYPE_ICONS[selected.channel_type] || '💬'}
                      </div>
                    )}
                  </div>

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{
                      fontWeight: 700,
                      fontSize: isMobile ? 16 : 15,
                      color: '#1e293b',
                      margin: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                    }}>
                      {selected.customer_name || 'Unknown'}
                    </p>
                    {isMobile ? (
                      /* Mobile: 2 บรรทัด — ช่องทาง + status */
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginTop: 3,
                        fontSize: 12, color: '#64748b',
                      }}>
                        <span style={{ fontWeight: 500 }}>
                          {selected.channel_name || selected.channel_type?.toUpperCase()}
                        </span>
                        {selected.handled_by !== 'agent' && selected.status !== 'closed' && selected.status !== 'blocked' && (
                          <>
                            <span>•</span>
                            <span style={{ color: '#f59e0b', fontWeight: 600 }}>🤖 บอท</span>
                          </>
                        )}
                        {selected.handled_by === 'agent' && selected.status !== 'closed' && selected.status !== 'blocked' && (
                          <>
                            <span>•</span>
                            <span style={{ color: '#22c55e', fontWeight: 600 }}>👤 {selected.agent_name ? selected.agent_name : 'แอดมิน'}</span>{/* show_acceptor_v1 */}
                          </>
                        )}
                        {selected.status === 'closed' && (
                          <>
                            <span>•</span>
                            <span style={{ color: '#94a3b8' }}>ปิดเคส</span>
                          </>
                        )}
                        {selected.status === 'blocked' && (
                          <>
                            <span>•</span>
                            <span style={{ color: '#ef4444' }}>บล็อค</span>
                          </>
                        )}
                      </div>
                    ) : (
                      /* Desktop: badge เดิม */
                      <span style={{
                        fontSize: 10, color: '#fff',
                        background: selected.channel_color || '#0ea5e9',
                        padding: '1px 7px', borderRadius: 5, fontWeight: 600,
                        display: 'inline-block', marginTop: 2,
                      }}>
                        {TYPE_ICONS[selected.channel_type]} {selected.channel_name || selected.channel_type?.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                {isMobile ? (
                  /* Mobile: 3-dot menu */
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button onClick={() => setShowActionMenu(!showActionMenu)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        width: 40, height: 40, borderRadius: 20,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#1e293b',
                      }}>
                      <MoreVertical size={22} />
                    </button>
                    {showActionMenu && (
                      <>
                        <div onClick={() => setShowActionMenu(false)}
                          style={{ position: 'fixed', inset: 0, zIndex: 100 }} />
                        <div style={{
                          position: 'absolute', top: 44, right: 0,
                          background: '#fff', borderRadius: 12,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          minWidth: 180, zIndex: 101,
                          overflow: 'hidden',
                          border: '1px solid #e2e8f0',
                        }}>
                          <button onClick={() => { openCustomerInfo(); setShowActionMenu(false); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '14px 16px', background: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, color: '#1e293b', textAlign: 'left' }}>
                            <span style={{ fontSize: 16 }}>👤</span>
                            <span>โปรไฟล์ลูกค้า</span>
                          </button>
                          {selected.status !== 'closed' && selected.status !== 'blocked' && (
                            <>
                              <div style={{ height: 1, background: '#f1f5f9' }} />
                              <button onClick={() => closeConv(selected.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '14px 16px', background: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, color: '#22c55e', textAlign: 'left' }}>
                                <span style={{ fontSize: 16 }}>✓</span>
                                <span>ปิดเคส</span>
                              </button>
                              <div style={{ height: 1, background: '#f1f5f9' }} />
                              <button onClick={() => blockConv(selected)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '14px 16px', background: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, color: '#ef4444', textAlign: 'left' }}>
                                <span style={{ fontSize: 16 }}>🚫</span>
                                <span>บล็อคลูกค้า</span>
                              </button>
                            </>
                          )}
                          {selected.status === 'blocked' && (
                            <>
                              <div style={{ height: 1, background: '#f1f5f9' }} />
                              <button onClick={() => unblockConv(selected)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '14px 16px', background: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, color: '#f59e0b', textAlign: 'left' }}>
                                <span style={{ fontSize: 16 }}>↩</span>
                                <span>ยกเลิกบล็อค</span>
                              </button>
                            </>
                          )}
                          {/* reopen_v1: เมนูเปิดเคสใหม่ แสดงเฉพาะเคสที่ปิดแล้ว */}
                          {selected.status === 'closed' && (
                            <>
                              <div style={{ height: 1, background: '#f1f5f9' }} />
                              <button onClick={() => reopenConv(selected.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '14px 16px', background: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, color: '#0ea5e9', textAlign: 'left', fontWeight: 500 }}>
                                <span style={{ fontSize: 16 }}>🔓</span>
                                <span>เปิดเคสใหม่</span>
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  /* Desktop: ปุ่มเดิม */
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={openCustomerInfo}
                      style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:6, padding:'6px 12px', fontSize:12, color:'#0ea5e9', cursor:'pointer', fontWeight: 600 }}>
                      👤 โปรไฟล์
                    </button>
                    {selected.status !== 'closed' && selected.status !== 'blocked' && (
                      <>
                        <button onClick={() => blockConv(selected)} style={{ padding: '6px 14px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>🚫 บล็อค</button>
                        <button onClick={() => closeConv(selected.id)} style={{ padding: '6px 14px', background: '#f0fdf4', color: '#22c55e', border: '1px solid #bbf7d0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✓ ปิดเคส</button>
                      </>
                    )}
                    {selected.status === 'blocked' && (
                      <button onClick={() => unblockConv(selected)} style={{ padding: '6px 14px', background: '#fffbeb', color: '#f59e0b', border: '1px solid #fde68a', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>↩ ยกเลิกบล็อค</button>
                    )}
                    {/* reopen_v1: ปุ่มเปิดเคสใหม่ desktop */}
                    {selected.status === 'closed' && (
                      <button onClick={() => reopenConv(selected.id)} style={{ padding: '6px 14px', background: '#e0f2fe', color: '#0ea5e9', border: '1px solid #bae6fd', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>🔓 เปิดเคสใหม่</button>
                    )}
                  </div>
                )}
              </div>

              {/* Messages */}
              <div ref={chatContainerRef} onScroll={() => { const container = chatContainerRef.current; if (container) { const dist = container.scrollHeight - container.scrollTop - container.clientHeight; userScrolledUpRef.current = dist > 150; } }}
                style={{
                  flex: 1, overflow: 'auto',
                  padding: isMobile ? '14px 12px' : '20px 24px',
                  display: 'flex', flexDirection: 'column', gap: 10,
                  WebkitOverflowScrolling: 'touch',
                }}>
                {hasMoreMessages && (
                  <div style={{ textAlign:'center', padding:'8px 0' }}>
                    <button onClick={loadMoreMsgs} disabled={loadingMore}
                      style={{ background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 18px', fontSize:13, color:'#64748b', cursor:'pointer', fontWeight: 500 }}>
                      {loadingMore ? 'กำลังโหลด...' : '⬆️ โหลดข้อความเก่า'}
                    </button>
                  </div>
                )}
                {safeMessages.map((m, idx) => (
                  <React.Fragment key={m.id}>
                    {(idx === 0 || safeMessages[idx-1].conv_id !== m.conv_id) && (
                      <div style={{ display:'flex', alignItems:'center', gap:8, margin:'8px 0' }}>
                        <div style={{ flex:1, height:1, background:'#e2e8f0' }} />
                        <span style={{ fontSize:11, color:'#94a3b8', whiteSpace:'nowrap', padding:'2px 10px', background:'#f1f5f9', borderRadius:10 }}>
                          {m.conv_status === 'closed' ? '✓ บทสนทนาที่ปิดแล้ว' : '● บทสนทนาปัจจุบัน'} — {new Date(m.conv_created_at).toLocaleDateString('th-TH', {day:'numeric',month:'short',year:'2-digit'})}
                        </span>
                        <div style={{ flex:1, height:1, background:'#e2e8f0' }} />
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: m.sender_type === 'customer' ? 'flex-start' : 'flex-end', alignItems: 'flex-end', gap: 8 }}>
                      {m.sender_type === 'customer' && (
                        /* avatar_v1: mini avatar ข้างข้อความลูกค้า */
                        <Avatar
                          name={selected.customer_name}
                          pic={selected.customer_profile_pic}
                          size={28}
                          color="#64748b"
                          fontSize={12}
                        />
                      )}
                      <div style={{ maxWidth: isMobile ? '78%' : '65%' }}>
                        {m.sender_type !== 'customer' && (
                          <p style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, textAlign: 'right', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120, marginLeft: 'auto' }}>
                            {m.sender_type === 'ai' ? '🤖 AI' : `👤 ${m.agent_name || agent?.name || 'Admin'}`}
                          </p>
                        )}
                        <div style={{
                          padding: '10px 14px', borderRadius: 16,
                          fontSize: isMobile ? 15 : 14,
                          lineHeight: 1.5,
                          background: m.sender_type === 'customer' ? '#fff' : m.sender_type === 'ai' ? '#ede9fe' : (selected.channel_color || '#0ea5e9'),
                          color: m.sender_type === 'customer' ? '#1e293b' : m.sender_type === 'ai' ? '#7c3aed' : '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          borderBottomLeftRadius: m.sender_type === 'customer' ? 4 : 16,
                          borderBottomRightRadius: m.sender_type === 'customer' ? 16 : 4,
                          wordBreak: 'break-word',
                        }}>
                          {m.file_url && m.file_type === 'image' && (
                            <>
                              {/* image_preview_v1 */}
                              <img src={getFileUrl(m.file_url)} loading="lazy" alt="img" onClick={() => setImagePreview(getFileUrl(m.file_url))} style={{ maxWidth: 200, borderRadius: 8, display: 'block', marginBottom: m.content ? 8 : 0, cursor: 'zoom-in' }} />
                              {/* slip_v1: ปุ่ม/ผลตรวจสลิป (เฉพาะรูปของลูกค้า) */}
                              {m.sender_type === 'customer' && (
                                <SlipStatus
                                  messageId={m.id}
                                  mode={slipSettings.mode}
                                  hasToken={slipSettings.has_token}
                                  verification={verifications[m.id]}
                                  busy={verifyingId === m.id}
                                  stalled={!!slipStalled[m.id]} /* slip_retry_v1 */
                                  quotaExceeded={slipQuota && !slipQuota.has_quota}
                                  onVerify={() => verifySlip(m.id)}
                                  onShowDetail={() => setSlipDetailModal(verifications[m.id])}
                                />
                              )}
                            </>
                          )}
                          {m.file_url && m.file_type !== 'image' && (
                            <a href={getFileUrl(m.file_url)} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'inherit', textDecoration: 'none', background: 'rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 8, marginBottom: m.content ? 8 : 0 }}>
                              <FileText size={16} /> ดาวน์โหลดไฟล์
                            </a>
                          )}
                          {m.content}
                        </div>
                        <p style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, textAlign: m.sender_type === 'customer' ? 'left' : 'right', paddingRight: 4 }}>
                          {new Date(m.created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
                <div ref={msgEndRef} />
              </div>
              {/* slip_v1: Slip Detail Modal */}
              {slipDetailModal && (() => {
                const v = slipDetailModal;
                const isSuccess = v.status === 'success';
                const isMismatch = v.status === 'mismatch';
                const isFraud = v.status === 'fraud';
                const isInvalid = v.status === 'invalid';
                const isError = v.status === 'error';
                const headerBg = isSuccess ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                              : isMismatch ? 'linear-gradient(135deg, #f59e0b, #ea580c)'
                              : isFraud ? 'linear-gradient(135deg, #dc2626, #991b1b)'
                              : isInvalid ? 'linear-gradient(135deg, #64748b, #475569)'
                              : 'linear-gradient(135deg, #ef4444, #dc2626)';
                const title = isSuccess ? '✅ สลิปถูกต้อง'
                            : isMismatch ? '⚠️ สลิปจริง — แต่บัญชีไม่ตรง'
                            : isFraud ? '🚫 สลิปปลอม — ตรวจจาก Slip2Go'
                            : isInvalid ? '❓ ไม่ใช่สลิป หรืออ่านไม่ออก'
                            : '❌ ตรวจสลิปไม่ผ่าน';
                return (
                  <div onClick={() => setSlipDetailModal(null)}
                    style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
                    <div onClick={e => e.stopPropagation()}
                      style={{ background:'#fff', borderRadius:14, maxWidth:480, width:'100%', overflow:'hidden', maxHeight:'90vh', overflowY:'auto' }}>
                      <div style={{ background: headerBg, color:'#fff', padding:'18px 22px' }}>
                        <h3 style={{ margin:0, fontSize:17, fontWeight:700 }}>{title}</h3>
                      </div>
                      <div style={{ padding:22 }}>
                        {isError ? (
                          <p style={{ margin:'0 0 16px', color:'#475569', fontSize:14 }}>{v.error_message || 'ตรวจสลิปไม่สำเร็จ'}</p>
                        ) : (
                          <>
                            {v.amount && (
                              <div style={{ background:'#f1f5f9', padding:'14px 18px', borderRadius:10, marginBottom:14, textAlign:'center' }}>
                                <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>จำนวนเงิน</div>
                                <div style={{ fontSize:28, fontWeight:700, color:'#0f172a' }}>฿{Number(v.amount).toLocaleString(undefined, { minimumFractionDigits:2 })}</div>
                              </div>
                            )}
                            <div style={{ display:'grid', gap:10, fontSize:13 }}>
                              {v.trans_time && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <span style={{ color:'#64748b' }}>เวลาโอน</span>
                                  <span style={{ color:'#1e293b', fontWeight:500 }}>{new Date(v.trans_time).toLocaleString('th-TH')}</span>
                                </div>
                              )}
                              {v.trans_ref && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <span style={{ color:'#64748b' }}>เลขอ้างอิง</span>
                                  <span style={{ color:'#1e293b', fontFamily:'monospace', fontSize:11 }}>{v.trans_ref}</span>
                                </div>
                              )}
                              {v.sender_name && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <span style={{ color:'#64748b' }}>ผู้โอน</span>
                                  <span style={{ color:'#1e293b' }}>{v.sender_name} {v.sender_bank ? `(${v.sender_bank})` : ''}</span>
                                </div>
                              )}
                              {v.receiver_name && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <span style={{ color:'#64748b' }}>ผู้รับ</span>
                                  <span style={{ color:'#1e293b', fontWeight:500 }}>{v.receiver_name}</span>
                                </div>
                              )}
                              {v.receiver_account && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <span style={{ color:'#64748b' }}>บัญชีปลายทาง</span>
                                  <span style={{ color:'#1e293b', fontFamily:'monospace' }}>{v.receiver_bank} {v.receiver_account}</span>
                                </div>
                              )}
                            </div>
                            {isSuccess && v.matched_account_name && (
                              <div style={{ marginTop:14, padding:'10px 14px', background:'#dcfce7', borderRadius:8, color:'#166534', fontSize:13 }}>
                                ✅ ตรงบัญชี: <strong>{v.matched_account_name}</strong>
                              </div>
                            )}
                            {isMismatch && (
                              <div style={{ marginTop:14, padding:'10px 14px', background:'#fee2e2', borderRadius:8, color:'#991b1b', fontSize:13 }}>
                                ⚠️ บัญชีปลายทางไม่ตรงกับบัญชีรับเงินของร้าน — กรุณาตรวจสอบ
                              </div>
                            )}
                            {isMismatch && (
                              <div style={{ marginTop:8, fontSize:12, color:'#64748b' }}>
                                💡 ถ้านี่คือบัญชีของร้าน → <a href="/settings" style={{ color:'#0ea5e9' }}>เพิ่มบัญชีใน Settings</a>
                              </div>
                            )}
                          </>
                        )}
                        <button onClick={() => setSlipDetailModal(null)}
                          style={{ marginTop:18, width:'100%', padding:10, borderRadius:8, background:'#0ea5e9', color:'#fff', border:'none', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                          ปิด
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* image_preview_v1: Image fullscreen viewer */}
              {/* esc_listener_v1: ปุ่ม ESC ย้ายไปอยู่ใน useEffect แล้ว (ดูด้านบน)
                  ของเดิมผูก keydown ระหว่าง render และเก็บ handler ไว้บน window
                  ถ้าออกจากหน้านี้ตอนเปิดดูรูปค้างอยู่ listener จะค้างบน window ตลอดไป */}
              {imagePreview && (
                  <div onClick={() => setImagePreview(null)}
                       style={{
                         position: 'fixed', inset: 0,
                         background: 'rgba(0,0,0,0.85)', zIndex: 9999,
                         display: 'flex', alignItems: 'center', justifyContent: 'center',
                         cursor: 'zoom-out', padding: 20,
                       }}>
                    <button onClick={(e) => { e.stopPropagation(); setImagePreview(null); }}
                            style={{
                              position: 'absolute', top: 16, right: 16,
                              background: 'rgba(255,255,255,0.15)', border: 'none',
                              color: '#fff', width: 40, height: 40, borderRadius: '50%',
                              cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                      <X size={24} />
                    </button>
                    <img src={imagePreview} alt="preview"
                         onClick={(e) => e.stopPropagation()}
                         style={{
                           maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain',
                           borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                         }} />
                  </div>
              )}
              {/* Quick Replies Panel */}
              {showQR && (
                <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', maxHeight: 260, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, overflow: 'auto' }}>
                      {qrTags.map(t => (
                        <button key={t} onClick={() => setQrTag(t)}
                          style={{
                            padding: isMobile ? '6px 14px' : '4px 12px',
                            borderRadius: 20, border: 'none', cursor: 'pointer',
                            fontSize: isMobile ? 12 : 11,
                            fontWeight: qrTag === t ? 700 : 400,
                            background: qrTag === t ? (t === 'all' ? '#1e293b' : getTagColor(t)) : '#f1f5f9',
                            color: qrTag === t ? '#fff' : '#64748b',
                            whiteSpace: 'nowrap',
                          }}>
                          {t === 'all' ? '📋 ทั้งหมด' : t}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setShowQR(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', marginLeft: 8, padding: 6 }}><X size={18} /></button>
                  </div>
                  <div style={{ overflow: 'auto', padding: 8, WebkitOverflowScrolling: 'touch' }}>
                    {filteredQR.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center', padding: 16, fontSize: 13 }}>ไม่มีข้อความด่วน</p>}
                    {filteredQR.map(r => (
                      <div key={r.id}
                        onClick={() => { setReply(r.content); if (r.file_url) setPendingFile({ url: r.file_url, type: r.file_type || 'image', name: r.title || 'QR image' }); setShowQR(false); }} /* quickreplies_image_v1 */
                        style={{
                          padding: isMobile ? '12px 14px' : '10px 14px',
                          borderRadius: 8, cursor: 'pointer', marginBottom: 6,
                          background: '#f8fafc', border: '1px solid #e2e8f0',
                          transition: 'background 0.15s',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 10, background: getTagColor(r.tag) + '20', color: getTagColor(r.tag), padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>{r.tag}</span>
                          <span style={{ fontWeight: 600, fontSize: isMobile ? 14 : 13, color: '#1e293b' }}>{r.title}</span>
                        </div>
                        <p style={{ fontSize: isMobile ? 13 : 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{r.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending File Preview */}
              {pendingFile && (
                <div style={{ background: '#f0f9ff', borderTop: '1px solid #bae6fd', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  {pendingFile.type === 'image' ? <Image size={18} color="#0ea5e9" /> : <FileText size={18} color="#0ea5e9" />}
                  <span style={{ fontSize: 13, color: '#0ea5e9', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pendingFile.name}</span>
                  <button onClick={() => setPendingFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}><X size={16} /></button>
                </div>
              )}

              {/* Input Area */}
              {selected.status !== 'closed' && selected.status !== 'blocked' ? (
                <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
                  {selected.handled_by !== 'agent' && (
                    <div style={{
                      padding: isMobile ? '10px 14px' : '12px 16px',
                      background: '#fffbeb', borderBottom: '1px solid #fde68a',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 20, flexShrink: 0 }}>🤖</span>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#92400e' }}>บอทกำลังดูแลอยู่</p>
                          {!isMobile && <p style={{ margin: 0, fontSize: 12, color: '#b45309' }}>กด "รับเคส" เพื่อเข้ามาตอบแชทด้วยตัวเอง</p>}
                        </div>
                      </div>
                      <button onClick={() => acceptCase(selected.id)}
                        style={{
                          background: '#0ea5e9', color: '#fff', border: 'none',
                          borderRadius: 8,
                          padding: isMobile ? '10px 18px' : '8px 20px',
                          fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 6,
                          flexShrink: 0,
                        }}>
                        ✋ รับเคส
                      </button>
                    </div>
                  )}
                  <div style={{
                    padding: isMobile ? '8px 10px' : '8px 16px',
                    display: 'flex', gap: isMobile ? 6 : 6,
                    alignItems: 'center',
                    paddingBottom: isMobile ? 'calc(8px + env(safe-area-inset-bottom, 0px))' : '8px',
                  }}>
                    {/* Quick Reply Button */}
                    <button onClick={() => setShowQR(!showQR)} title="ข้อความด่วน"
                      style={{
                        width: isMobile ? 40 : 36,
                        height: isMobile ? 40 : 36,
                        borderRadius: 10,
                        background: showQR ? '#fef9c3' : '#f1f5f9',
                        color: showQR ? '#f59e0b' : '#64748b',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                      <Zap size={isMobile ? 18 : 16} />
                    </button>

                    {/* Image Upload */}
                    <button onClick={() => { fileInputRef.current.accept = 'image/*'; fileInputRef.current.click(); }} title="แนบรูปภาพ"
                      style={{
                        width: isMobile ? 40 : 36,
                        height: isMobile ? 40 : 36,
                        borderRadius: 10,
                        background: '#f1f5f9', color: '#64748b',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                      <Image size={isMobile ? 18 : 16} />
                    </button>

                    {/* File Upload — ซ่อนบน mobile (มีปุ่ม Image อยู่แล้ว ประหยัดพื้นที่) */}
                    {!isMobile && (
                      <button onClick={() => { fileInputRef.current.accept = '*/*'; fileInputRef.current.click(); }} title="แนบไฟล์"
                        style={{ width: 36, height: 36, borderRadius: 8, background: '#f1f5f9', color: '#64748b', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Paperclip size={16} />
                      </button>
                    )}

                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />

                    {/* Text Input */}
                    <input value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
                      onPaste={e => { /* image_paste_v1 */
                        const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'));
                        if (!item) return;
                        e.preventDefault();
                        const file = item.getAsFile();
                        if (file) handleFileUpload(file);
                      }}
                      placeholder={uploading ? 'กำลังอัปโหลด...' : isMobile ? 'พิมพ์ข้อความ...' : 'พิมพ์ข้อความ หรือกด ⚡ เพื่อใช้ข้อความด่วน...'}
                      disabled={uploading}
                      style={{
                        flex: 1,
                        padding: isMobile ? '11px 16px' : '10px 16px',
                        borderRadius: 24,
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc', color: '#1e293b',
                        fontSize: isMobile ? 16 : 14,  // mobile_v1: 16px ป้องกัน iOS auto-zoom
                        outline: 'none',
                        minWidth: 0,
                      }} />

                    {/* Send Button */}
                    <button onClick={sendReply} disabled={uploading}
                      style={{
                        width: isMobile ? 44 : 40,
                        height: isMobile ? 44 : 40,
                        borderRadius: '50%',
                        background: (uploading || (!reply.trim() && !pendingFile)) ? '#e2e8f0' : (selected.channel_color || '#0ea5e9'),
                        color: '#fff', border: 'none',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                      <Send size={isMobile ? 18 : 16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: isMobile ? 14 : 12,
                  background: '#fff', borderTop: '1px solid #e2e8f0',
                  textAlign: 'center', color: '#94a3b8',
                  fontSize: 13,
                  paddingBottom: isMobile ? 'calc(14px + env(safe-area-inset-bottom, 0px))' : 12,
                }}>
                  {selected.status === 'closed' ? '✓ เคสนี้ปิดแล้ว' : '🚫 ลูกค้านี้ถูกบล็อค'}
                </div>
              )}
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              <MessageSquare size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
              <p style={{ fontSize: 15 }}>เลือกบทสนทนาเพื่อเริ่มต้น</p>
            </div>
          )}

          {/* Customer Profile Panel — Desktop only (right sidebar) */}
          {showCustomerPanel && !isMobile && (
            <CustomerProfilePanel
              customerProfile={customerProfile}
              customerForm={customerForm} setCustomerForm={setCustomerForm}
              editingCustomer={editingCustomer} setEditingCustomer={setEditingCustomer}
              newTag={newTag} setNewTag={setNewTag}
              saveCustomerProfile={saveCustomerProfile}
              onClose={() => { setShowCustomerPanel(false); setCustomerProfile(null); setEditingCustomer(false); }}
              isMobile={false}
            />
          )}
        </div>
      )}

      {/* create_case_ui_v1: โมดัลเปิดเคสใหม่ — เลือกลูกค้า + ช่องทาง */}
      {showCreateCase && (() => {
        const q = searchCustomer.trim().toLowerCase();
        const filtered = !q ? customers : customers.filter(cu =>
          String(cu.name || '').toLowerCase().includes(q) ||
          String(cu.external_id || '').toLowerCase().includes(q)
        );
        return (
          <div onClick={closeCreateCase}
            style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:440, maxHeight:'88vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>

              <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:8 }}>
                <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:'#1e293b', flex:1 }}>➕ เปิดเคสใหม่</h3>
                <button onClick={closeCreateCase}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:4, display:'flex' }}>
                  <X size={20} />
                </button>
              </div>

              <div style={{ padding:'14px 20px 0' }}>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  ลูกค้า
                </label>
                <div style={{ position:'relative', marginBottom:8 }}>
                  <Search size={15} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} />
                  <input value={searchCustomer} onChange={e => setSearchCustomer(e.target.value)}
                    placeholder="ค้นหาชื่อ หรือ ID ลูกค้า..."
                    style={{ width:'100%', padding: isMobile ? '11px 12px 11px 32px' : '9px 12px 9px 32px', borderRadius:8, border:'1px solid #e2e8f0', fontSize: isMobile ? 16 : 13, outline:'none', boxSizing:'border-box' }} />
                </div>
              </div>

              <div style={{ flex:1, overflow:'auto', padding:'0 20px', minHeight:120, WebkitOverflowScrolling:'touch' }}>
                {customers.length === 0 && (
                  <p style={{ textAlign:'center', color:'#94a3b8', fontSize:13, padding:'20px 0' }}>กำลังโหลด...</p>
                )}
                {customers.length > 0 && filtered.length === 0 && (
                  <p style={{ textAlign:'center', color:'#94a3b8', fontSize:13, padding:'20px 0' }}>ไม่พบลูกค้าที่ค้นหา</p>
                )}
                {filtered.slice(0, 100).map(cu => {
                  const active = String(newCase.customer_id) === String(cu.id);
                  return (
                    <div key={cu.id} onClick={() => setNewCase({ ...newCase, customer_id: cu.id })}
                      style={{
                        display:'flex', alignItems:'center', gap:10,
                        padding: isMobile ? '11px 12px' : '9px 12px',
                        marginBottom:6, borderRadius:8, cursor:'pointer',
                        background: active ? '#f0f9ff' : '#f8fafc',
                        border: active ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                      }}>
                      <Avatar name={cu.name || cu.external_id} pic={cu.profile_pic} size={32} color="#0ea5e9" />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize: isMobile ? 14 : 13, fontWeight:600, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {cu.name || '— ไม่มีชื่อ —'}
                        </div>
                        <div style={{ fontSize:11, color:'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {cu.external_id}
                        </div>
                      </div>
                      {active && <span style={{ color:'#0ea5e9', fontSize:16, flexShrink:0 }}>✓</span>}
                    </div>
                  );
                })}
                {filtered.length > 100 && (
                  <p style={{ textAlign:'center', color:'#94a3b8', fontSize:11, padding:'4px 0 8px' }}>
                    แสดง 100 รายแรก — พิมพ์ค้นหาเพื่อจำกัดผลลัพธ์
                  </p>
                )}
              </div>

              <div style={{ padding:'14px 20px', borderTop:'1px solid #f1f5f9' }}>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  ช่องทาง
                </label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
                  {channels.length === 0 && <span style={{ fontSize:13, color:'#94a3b8' }}>ไม่มีช่องทาง</span>}
                  {channels.map(ch => {
                    const active = String(newCase.channel_id) === String(ch.id);
                    return (
                      <button key={ch.id} onClick={() => setNewCase({ ...newCase, channel_id: ch.id })}
                        style={{
                          padding: isMobile ? '8px 14px' : '6px 12px', borderRadius:20, cursor:'pointer',
                          fontSize: isMobile ? 13 : 12, fontWeight: active ? 700 : 500,
                          background: active ? (ch.color || '#0ea5e9') : '#f1f5f9',
                          color: active ? '#fff' : '#64748b',
                          border: 'none',
                        }}>
                        {TYPE_ICONS[ch.type] || '💬'} {ch.name}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={closeCreateCase}
                    style={{ flex:1, padding: isMobile ? '13px' : '10px', background:'#f1f5f9', color:'#475569', border:'none', borderRadius:10, fontSize: isMobile ? 15 : 13, fontWeight:600, cursor:'pointer' }}>
                    ยกเลิก
                  </button>
                  <button onClick={handleCreateCase}
                    disabled={creatingCase || !newCase.customer_id || !newCase.channel_id}
                    style={{
                      flex:1, padding: isMobile ? '13px' : '10px',
                      background: (creatingCase || !newCase.customer_id || !newCase.channel_id) ? '#e2e8f0' : '#0ea5e9',
                      color:'#fff', border:'none', borderRadius:10,
                      fontSize: isMobile ? 15 : 13, fontWeight:700,
                      cursor: (creatingCase || !newCase.customer_id || !newCase.channel_id) ? 'not-allowed' : 'pointer',
                    }}>
                    {creatingCase ? 'กำลังเปิด...' : 'เปิดเคส'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* mobile_v1: Customer Info — Mobile fullscreen */}
      {showInfoMobile && (
        <CustomerProfilePanel
          customerProfile={customerProfile}
          customerForm={customerForm} setCustomerForm={setCustomerForm}
          editingCustomer={editingCustomer} setEditingCustomer={setEditingCustomer}
          newTag={newTag} setNewTag={setNewTag}
          saveCustomerProfile={saveCustomerProfile}
          onClose={() => { setMobileView('chat'); setEditingCustomer(false); }}
          isMobile={true}
        />
      )}

    </div>
  );
}

// mobile_v1: extract Customer Profile Panel เป็น component แยก
// รองรับทั้ง desktop sidebar และ mobile fullscreen
function CustomerProfilePanel({
  customerProfile, customerForm, setCustomerForm,
  editingCustomer, setEditingCustomer,
  newTag, setNewTag, saveCustomerProfile, onClose, isMobile
}) {
  return (
    <div style={{
      width: isMobile ? '100%' : 280,
      background:'#fff',
      borderLeft: isMobile ? 'none' : '1px solid #e2e8f0',
      display:'flex', flexDirection:'column',
      flexShrink:0,
      height: '100%',
    }}>
      <div style={{
        padding: isMobile ? '12px 12px 12px 4px' : '16px 20px',
        borderBottom:'1px solid #f1f5f9',
        display:'flex', alignItems:'center',
        gap: 8,
        minHeight: isMobile ? 56 : 'auto',
      }}>
        {isMobile && (
          <button onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              width: 40, height: 40, borderRadius: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#1e293b', flexShrink: 0,
            }}>
            <ChevronLeft size={26} />
          </button>
        )}
        <h3 style={{
          margin:0,
          fontSize: isMobile ? 17 : 14,
          fontWeight:700, color:'#1e293b',
          flex: 1,
        }}>
          {isMobile ? 'ข้อมูลลูกค้า' : '👤 โปรไฟล์ลูกค้า'}
        </h3>
        {!isMobile && (
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:16, padding: 4 }}>✕</button>
        )}
      </div>
      <div style={{ padding: isMobile ? '20px 16px' : 20, flex:1, overflow:'auto', WebkitOverflowScrolling: 'touch' }}>
        {!customerProfile && <p style={{ textAlign:'center', color:'#94a3b8', fontSize:13 }}>กำลังโหลด...</p>}
        {customerProfile && <>
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <div style={{
              width: isMobile ? 80 : 64,
              height: isMobile ? 80 : 64,
              borderRadius:'50%',
              background:'#0ea5e920',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize: isMobile ? 32 : 28,
              margin:'0 auto 10px',
              fontWeight: 700, color: '#0ea5e9',
            }}>
              {(customerForm.name || customerProfile.external_id || 'U')[0].toUpperCase()}
            </div>
            {isMobile && customerForm.name && (
              <p style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>{customerForm.name}</p>
            )}
            <p style={{ fontSize: isMobile ? 12 : 11, color:'#94a3b8', margin:0 }}>ID: {customerProfile.external_id}</p>
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>ชื่อลูกค้า</label>
            {editingCustomer ? (
              <input value={customerForm.name} onChange={e => setCustomerForm({...customerForm, name:e.target.value})}
                placeholder="ใส่ชื่อลูกค้า..."
                style={{
                  width:'100%',
                  padding: isMobile ? '12px 14px' : '7px 10px',
                  borderRadius:8,
                  border:'1px solid #e2e8f0',
                  fontSize: isMobile ? 16 : 13,
                  boxSizing:'border-box',
                  outline: 'none',
                }} />
            ) : (
              <p style={{ margin:0, fontSize: isMobile ? 15 : 14, fontWeight:600, color:'#1e293b' }}>{customerForm.name || '— ไม่มีชื่อ —'}</p>
            )}
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>แท็ก</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
              {customerForm.tags.map((tag, i) => (
                <span key={i} style={{
                  background:'#f0f9ff', color:'#0ea5e9',
                  borderRadius:14,
                  padding: isMobile ? '4px 12px' : '2px 10px',
                  fontSize: isMobile ? 13 : 12,
                  display:'flex', alignItems:'center', gap:6,
                  fontWeight: 500,
                }}>
                  {tag}
                  {editingCustomer && (
                    <button onClick={() => setCustomerForm({...customerForm, tags: customerForm.tags.filter((_,j) => j!==i)})}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:0, fontSize: 14, lineHeight: 1 }}>✕</button>
                  )}
                </span>
              ))}
              {customerForm.tags.length === 0 && <span style={{ fontSize:13, color:'#94a3b8' }}>ยังไม่มีแท็ก</span>}
            </div>
            {editingCustomer && (
              <div style={{ display:'flex', gap:6 }}>
                <input value={newTag} onChange={e => setNewTag(e.target.value)}
                  onKeyDown={e => { if(e.key==='Enter' && newTag.trim()) { setCustomerForm({...customerForm, tags:[...customerForm.tags, newTag.trim()]}); setNewTag(''); }}}
                  placeholder="พิมพ์แท็ก + Enter"
                  style={{
                    flex:1,
                    padding: isMobile ? '10px 12px' : '5px 8px',
                    borderRadius:8,
                    border:'1px solid #e2e8f0',
                    fontSize: isMobile ? 16 : 12,
                    outline: 'none',
                  }} />
                <button onClick={() => { if(newTag.trim()) { setCustomerForm({...customerForm, tags:[...customerForm.tags, newTag.trim()]}); setNewTag(''); }}}
                  style={{
                    background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
                    padding: isMobile ? '0 16px' : '5px 10px',
                    fontSize: isMobile ? 18 : 12,
                    cursor:'pointer', fontWeight: 600,
                  }}>+</button>
              </div>
            )}
          </div>

          <div style={{ marginBottom:24 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>โน้ต</label>
            {editingCustomer ? (
              <textarea value={customerForm.note} onChange={e => setCustomerForm({...customerForm, note:e.target.value})}
                rows={isMobile ? 5 : 4}
                placeholder="บันทึกข้อมูลลูกค้า..."
                style={{
                  width:'100%',
                  padding: isMobile ? '12px 14px' : '7px 10px',
                  borderRadius:8,
                  border:'1px solid #e2e8f0',
                  fontSize: isMobile ? 16 : 13,
                  resize:'vertical',
                  boxSizing:'border-box',
                  outline: 'none',
                  fontFamily: 'inherit',
                }} />
            ) : (
              <p style={{ margin:0, fontSize: isMobile ? 14 : 13, color: customerForm.note ? '#1e293b' : '#94a3b8', whiteSpace:'pre-wrap', lineHeight: 1.5 }}>{customerForm.note || '— ยังไม่มีโน้ต —'}</p>
            )}
          </div>

          {editingCustomer ? (
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setEditingCustomer(false)}
                style={{
                  flex:1,
                  padding: isMobile ? '14px' : '8px',
                  background:'#f1f5f9', border:'none',
                  borderRadius: 10,
                  fontSize: isMobile ? 15 : 13,
                  cursor:'pointer', fontWeight: 600,
                  color: '#475569',
                }}>ยกเลิก</button>
              <button onClick={saveCustomerProfile}
                style={{
                  flex:1,
                  padding: isMobile ? '14px' : '8px',
                  background:'#0ea5e9', color:'#fff', border:'none',
                  borderRadius: 10,
                  fontSize: isMobile ? 15 : 13,
                  fontWeight:700, cursor:'pointer',
                }}>บันทึก</button>
            </div>
          ) : (
            <button onClick={() => setEditingCustomer(true)}
              style={{
                width:'100%',
                padding: isMobile ? '14px' : '8px',
                background:'#f0f9ff', border:'1px solid #bae6fd',
                borderRadius: 10,
                fontSize: isMobile ? 15 : 13,
                color:'#0ea5e9', fontWeight:600, cursor:'pointer',
              }}>
              ✏️ แก้ไขโปรไฟล์
            </button>
          )}
        </>}
      </div>
    </div>
  );
}
